import { KeyExpiryCell } from "@via/pages/projects/metadata/configuration/data-import/components/login-list";
import type { VmetaImportLogin, VmetaImportPlatform } from "common";
import type { FC } from "react";

/**
 * The config's credential. Lists only what the person may use for this platform (the server's list is
 * exactly that). A current binding missing from the list belongs to someone else: shown by name, read only,
 * because keeping it needs no check but changing it would.
 */
const LoginPicker: FC<{
  platform: VmetaImportPlatform;
  value: number | null;
  items: VmetaImportLogin[];
  boundName?: string | null;
  onChange: (id: number) => void;
}> = ({ platform, value, items, boundName, onChange }) => {
  const choices = items.filter(i => i.platform === platform);
  const chosen = choices.find(i => i.id === value);
  if (value !== null && !chosen) {
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-900">Credential</p>
        <p className="text-sm text-gray-700">
          {boundName ?? "Credential unavailable"}
        </p>
        <p className="text-xs text-gray-500">
          Only someone who can use it may change it.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <label
        htmlFor="import-credential"
        className="block text-sm font-medium text-gray-900">
        Credential
      </label>
      <select
        id="import-credential"
        value={value ?? ""}
        onChange={e => e.target.value && onChange(Number(e.target.value))}
        className="block w-full rounded-md border-0 py-1.5 text-sm ring-1 ring-inset ring-gray-300">
        <option value="">Choose a credential</option>
        {choices.map(i => (
          <option key={i.id} value={i.id}>
            {`${i.name} (${i.scope === "personal" ? "Personal" : (i.organizationName ?? "Organization")})`}
          </option>
        ))}
      </select>
      {chosen && (
        <KeyExpiryCell
          platform={chosen.platform}
          keyExpiresAt={chosen.keyExpiresAt}
        />
      )}
      {choices.length === 0 && (
        <p className="text-xs text-gray-500">
          No credential for this platform yet. Add one under Credentials.
        </p>
      )}
    </div>
  );
};

export default LoginPicker;
